import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Check, ArrowRight, LogOut, Lock, Clock, Eye, AlertCircle, Pencil, AlertTriangle, CheckCircle2, ListChecks } from 'lucide-react';
import useBottomSheetSwipe from '../../hooks/useBottomSheetSwipe';
import { SHEET_TOKENS, getGlassSurface, getTextColors, getBackdropStyle, getHandleStyle } from './sheetStyle';
import { SheetTitleHeader } from './SheetUI';
import MereniPole from './MereniPole';

function hhmmNow() {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// Vyparsuje možnosti z JSON (pro typ 'vyber'). Prázdné → defaultní OK/NOK/N/A.
function parseMoznosti(json) {
    try {
        let m = JSON.parse(json || '[]');
        if (!Array.isArray(m)) m = [];
        m = m.map(x => typeof x === 'string' ? { text: x } : x);
        if (m.length === 0) return [{ text: 'OK' }, { text: 'NOK', negativni: true }, { text: 'N/A' }];
        return m;
    } catch { return [{ text: 'OK' }, { text: 'NOK', negativni: true }, { text: 'N/A' }]; }
}

export default function PhaseModal({ faze, readOnly, onClose, onSave, onDokoncit, isDark, isMobile }) {
    const [dokoncuji, setDokoncuji] = useState(false);
    const [dokoncitError, setDokoncitError] = useState(null);
    const [ukonceniEditOpen, setUkonceniEditOpen] = useState(false);
    const [ukonceniManualTime, setUkonceniManualTime] = useState('');
    const debounceRefs = useRef({});
    const { dragHandleProps, modalStyle: swipeStyle } = useBottomSheetSwipe(onClose, { enabled: !!isMobile });
    const jeDone = faze.stav === 'done';
    const jeFuture = !jeDone && readOnly; // Fáze ještě nezačala (předchozí nehotová)
    const jeUkonceni = faze.id === 'ukonceni';

    if (!faze) return null;

    const checklisty = faze.checklisty || [];
    // Náhledové šablony (před zahájením směny) nemají DB instanci, takže
    // se do celkového progressu "X/Y hotovo" nepočítají.
    const liveChecklisty = checklisty.filter(c => !c.preview);
    const totalAll = liveChecklisty.reduce((a, c) => a + (c.total || 0), 0);
    const doneAll = liveChecklisty.reduce((a, c) => a + (c.done_count || 0), 0);

    // Okamžité uložení (checkbox/kontrola/vyber/foto)
    const saveNow = (chId, polId, data) => {
        if (readOnly) return;
        onSave?.(chId, polId, data);
    };
    // Debounced uložení (cislo/text) — uložíme po 500 ms klidu,
    // optimistické uložení v useShiftData zajistí okamžitou odezvu UI.
    const saveDebounced = (chId, polId, data) => {
        if (readOnly) return;
        const key = `${chId}_${polId}`;
        clearTimeout(debounceRefs.current[key]);
        debounceRefs.current[key] = setTimeout(() => onSave?.(chId, polId, data), 500);
    };

    const glass = getGlassSurface(isDark);
    // Portal do body — uniká stacking-context trapu.
    return createPortal((
        <div onClick={onClose} style={{
            ...getBackdropStyle(),
            zIndex: 1000,
            alignItems: 'center', justifyContent: 'flex-end',
            padding: 0,
        }}>
            <div onClick={e => e.stopPropagation()} className="ms-sheet-anim" style={{
                width: '100%', maxWidth: isMobile ? '100%' : 520,
                minHeight: isMobile ? '50vh' : 'auto',
                maxHeight: isMobile ? '85vh' : '90vh',
                paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                borderRadius: `${SHEET_TOKENS.radius.container}px ${SHEET_TOKENS.radius.container}px 0 0`,
                ...glass,
                display: 'flex', flexDirection: 'column',
                overflow: 'hidden',
                ...swipeStyle,
            }}>
                {isMobile && (
                    <div {...dragHandleProps} style={{
                        display: 'flex', justifyContent: 'center',
                        paddingTop: 10, paddingBottom: 6,
                        cursor: 'grab', flexShrink: 0,
                    }}>
                        <span style={getHandleStyle(isDark)} />
                    </div>
                )}

                <SheetTitleHeader
                    isDark={isDark}
                    icon={<ListChecks size={16} />}
                    iconColor={/^#[0-9a-f]{6}$/i.test(faze.barva || '') ? faze.barva : '#0CC0DF'}
                    title={faze.nazev}
                    subtitle={`${faze.cas_od}–${faze.cas_do}${totalAll > 0 ? ` · ${doneAll}/${totalAll} hotovo` : ''}`}
                    onClose={onClose}
                />

                <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
                    {checklisty.length === 0 && (
                        <div style={{ textAlign: 'center', padding: 28, color: isDark ? 'rgba(255,255,255,0.4)' : 'var(--text-muted)' }}>
                            <div style={{ fontSize: 28, marginBottom: 8 }}>✨</div>
                            <div style={{ fontSize: 13 }}>Žádné checklisty v této fázi</div>
                        </div>
                    )}
                    {checklisty.map((ch, chIdx) => {
                        const isPreview = !!ch.preview;
                        return (
                        <div key={ch.id ?? `preview-${ch.sablona_id ?? chIdx}`} style={{ marginBottom: 16 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: isDark ? 'rgba(255,255,255,0.85)' : 'var(--text-primary)', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    {ch.nazev}
                                    {isPreview && (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 999, background: isDark ? 'rgba(12,192,223,0.12)' : 'rgba(12,192,223,0.1)', color: '#0CC0DF', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                                            <Eye size={10} /> Náhled šablony
                                        </span>
                                    )}
                                </span>
                                {!isPreview && (
                                    <span style={{ fontSize: 11, color: isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                                        {ch.done_count}/{ch.total}
                                    </span>
                                )}
                            </div>
                            {(ch.items || []).map((item, itemIdx) => (
                                <PolozkaRow
                                    key={item.id ?? `preview-item-${itemIdx}`}
                                    ch={ch}
                                    item={item}
                                    isPreview={isPreview}
                                    readOnly={readOnly}
                                    isDark={isDark}
                                    onSaveNow={saveNow}
                                    onSaveDebounced={saveDebounced}
                                />
                            ))}
                        </div>
                        );
                    })}

                </div>

                <div style={{ padding: '12px 16px', borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {jeDone && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px', borderRadius: 7, background: 'rgba(16,185,129,0.1)', color: '#10B981', fontSize: 12, fontWeight: 600 }}>
                            <Check size={14} /> Fáze dokončena — úpravy již nejsou možné
                        </div>
                    )}
                    {jeFuture && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px', borderRadius: 7, background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', color: isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-muted)', fontSize: 12, fontWeight: 500 }}>
                            <Lock size={14} /> Nejdřív dokončete předchozí fázi
                        </div>
                    )}
                    {dokoncitError && (
                        <div style={{
                            padding: '10px 12px', borderRadius: 7,
                            background: isDark ? 'rgba(239,68,68,0.10)' : 'rgba(239,68,68,0.08)',
                            border: `1px solid ${isDark ? 'rgba(239,68,68,0.3)' : 'rgba(239,68,68,0.25)'}`,
                            color: isDark ? 'rgba(252,165,165,0.95)' : '#B91C1C',
                            fontSize: 12, lineHeight: 1.4,
                            display: 'flex', alignItems: 'flex-start', gap: 8,
                        }}>
                            <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, marginBottom: dokoncitError.chybejici?.length ? 4 : 0 }}>
                                    {dokoncitError.message}
                                </div>
                                {dokoncitError.chybejici?.length > 0 && (
                                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                                        {dokoncitError.chybejici.map((p, i) => (
                                            <li key={i}>{p.text}{p.checklist_nazev ? ` (${p.checklist_nazev})` : ''}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <button type="button" onClick={() => setDokoncitError(null)} aria-label="Zavřít" style={{
                                background: 'none', border: 'none', padding: 2, cursor: 'pointer',
                                color: isDark ? 'rgba(252,165,165,0.7)' : '#B91C1C',
                                flexShrink: 0, lineHeight: 0, marginLeft: 4,
                            }}>
                                <X size={14} />
                            </button>
                        </div>
                    )}
                    {!readOnly && onDokoncit && !ukonceniEditOpen && (
                        <button type="button" disabled={dokoncuji} onClick={async () => {
                            setDokoncuji(true);
                            setDokoncitError(null);
                            try {
                                // U ukončení směny posíláme plánovaný cas_do jako default —
                                // typicky uživatel ukončuje na konci směny, sazby/odvody mají
                                // sedět s plánem. Manuální čas se nabízí přes "Ukončit s jiným časem".
                                const defaultClock = jeUkonceni && faze.cas_do ? faze.cas_do.slice(0, 5) : null;
                                const res = await onDokoncit(faze.id, defaultClock);
                                if (!res?.ok) {
                                    setDokoncitError({
                                        message: res?.error || 'Fázi se nepodařilo dokončit.',
                                        chybejici: res?.chybejici_polozky || [],
                                    });
                                }
                            } finally {
                                setDokoncuji(false);
                            }
                        }} style={{
                            width: '100%', padding: '12px', borderRadius: 7,
                            background: jeUkonceni
                                ? 'linear-gradient(90deg, #FF8A00, #975ACD)'
                                : faze.barva || '#0CC0DF',
                            color: '#fff', border: 'none',
                            fontSize: 14, fontWeight: 700, cursor: dokoncuji ? 'wait' : 'pointer',
                            opacity: dokoncuji ? 0.7 : 1,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        }}>
                            {jeUkonceni
                                ? (<><LogOut size={16} /> Ukončit směnu v {faze.cas_do?.slice(0, 5) || '—'}</>)
                                : (<>Dokončit fázi <ArrowRight size={16} /></>)}
                        </button>
                    )}

                    {!readOnly && onDokoncit && jeUkonceni && !ukonceniEditOpen && (
                        <button type="button" onClick={() => {
                            // Předvyplníme aktuálním časem — manuální editace je výjimka
                            // (např. ukončení později než plán), ne default. Default je
                            // plánovaný cas_do v hlavním tlačítku výše.
                            setUkonceniManualTime(hhmmNow());
                            setUkonceniEditOpen(true);
                        }} disabled={dokoncuji} style={{
                            alignSelf: 'center',
                            background: 'none', border: 'none',
                            color: isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)',
                            fontSize: 12, cursor: 'pointer',
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '6px 10px',
                        }}>
                            <Pencil size={12} /> Ukončit s jiným časem
                        </button>
                    )}

                    {!readOnly && onDokoncit && jeUkonceni && ukonceniEditOpen && (
                        <div style={{
                            padding: 12, borderRadius: 9,
                            background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`,
                            display: 'flex', flexDirection: 'column', gap: 8,
                        }}>
                            <div style={{ fontSize: 12, color: isDark ? 'rgba(255,255,255,0.7)' : 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                                <Clock size={12} /> Vlastní čas ukončení směny
                            </div>
                            <input
                                type="time"
                                value={ukonceniManualTime}
                                onChange={e => setUkonceniManualTime(e.target.value)}
                                style={{
                                    padding: '10px 12px', borderRadius: 7,
                                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'var(--border-default)'}`,
                                    background: isDark ? 'rgba(0,0,0,0.3)' : 'var(--surface-base)',
                                    color: isDark ? '#fff' : 'var(--text-primary)',
                                    fontSize: 14, fontVariantNumeric: 'tabular-nums',
                                    width: '100%', boxSizing: 'border-box',
                                }}
                            />
                            <div style={{ fontSize: 11, color: isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)', lineHeight: 1.4 }}>
                                Pro audit se uloží i automatický čas — nelze retroaktivně fejkovat.
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button type="button" onClick={() => { setUkonceniEditOpen(false); setUkonceniManualTime(''); }}
                                    disabled={dokoncuji}
                                    style={{ flex: 1, padding: '10px', borderRadius: 7, background: 'transparent',
                                        color: isDark ? 'rgba(255,255,255,0.65)' : 'var(--text-secondary)',
                                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'var(--border-default)'}`,
                                        fontSize: 13, cursor: 'pointer' }}>
                                    Zrušit
                                </button>
                                <button type="button" disabled={dokoncuji || !ukonceniManualTime} onClick={async () => {
                                    setDokoncuji(true);
                                    setDokoncitError(null);
                                    try {
                                        const res = await onDokoncit(faze.id, ukonceniManualTime);
                                        if (!res?.ok) {
                                            setDokoncitError({
                                                message: res?.error || 'Fázi se nepodařilo dokončit.',
                                                chybejici: res?.chybejici_polozky || [],
                                            });
                                        }
                                    } finally {
                                        setDokoncuji(false);
                                    }
                                }} style={{ flex: 2, padding: '10px', borderRadius: 7,
                                    background: 'linear-gradient(90deg, #FF8A00, #975ACD)',
                                    color: '#fff', border: 'none',
                                    fontSize: 13, fontWeight: 700,
                                    cursor: dokoncuji || !ukonceniManualTime ? 'not-allowed' : 'pointer',
                                    opacity: dokoncuji || !ukonceniManualTime ? 0.6 : 1,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                }}>
                                    <LogOut size={14} /> Ukončit v {ukonceniManualTime || '—'}
                                </button>
                            </div>
                        </div>
                    )}
                    <button type="button" onClick={onClose} style={{ width: '100%', padding: '10px', borderRadius: 7, background: 'transparent', color: isDark ? 'rgba(255,255,255,0.65)' : 'var(--text-secondary)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'var(--border-default)'}`, fontSize: 13, cursor: 'pointer' }}>
                        Zavřít
                    </button>
                </div>
            </div>
        </div>
    ), document.body);
}

// Jedna položka checklistu — renderuje se podle typu:
// checkbox, kontrola (OK/NOK/N/A), cislo (+ parametr limity), vyber, text, foto.
// Při NOK stavu (kontrola NOK, negativní výběr, mimo_limit) se vždy zobrazí
// blok s instrukcí a povinnou reakcí (poznamka_akce). Pro ostatní položky
// je poznámka skrytá pod toggle tlačítkem.
function PolozkaRow({ ch, item, isPreview, readOnly, isDark, onSaveNow, onSaveDebounced }) {
    const disabled = readOnly || isPreview;
    const odp = item.odpoved || {};
    const typ = item.typ_odpovedi || 'checkbox';
    const mimoLimit = !!odp.mimo_limit;
    const mutedColor = isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)';

    // Je stav "negativní" — ať už NOK u kontroly, negativni u vyberu, nebo mimo_limit u cisla
    const moznosti = typ === 'vyber' ? parseMoznosti(item.moznosti_json) : [];
    const vybranaMoznost = typ === 'vyber' ? moznosti.find(m => m.text === odp.hodnota_vyber) : null;
    const jeNokKontrola = typ === 'kontrola' && odp.hodnota_vyber === 'NOK';
    const jeNegativniVyber = typ === 'vyber' && vybranaMoznost?.negativni;
    const jeNok = jeNokKontrola || jeNegativniVyber || (typ === 'cislo' && mimoLimit);

    // Stavová ikonka vlevo
    const StatusIcon = () => {
        if (!item.splneno) return <div style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)'}`, flexShrink: 0 }} />;
        if (jeNok) return <AlertTriangle size={16} style={{ color: '#EF4444', flexShrink: 0 }} />;
        return <CheckCircle2 size={16} style={{ color: '#10B981', flexShrink: 0 }} />;
    };

    const rowBase = {
        padding: '10px 12px', marginBottom: 6, borderRadius: 7,
        background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
        border: `1px ${isPreview ? 'dashed' : 'solid'} ${jeNok ? 'rgba(239,68,68,0.25)' : isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}`,
        borderLeft: jeNok ? '3px solid #EF4444' : item.splneno ? '3px solid #10B981' : `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}`,
        opacity: disabled ? 0.85 : 1,
    };

    return (
        <div style={rowBase}>
            {/* Horní řádek — ikonka + text + inline ovládání */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <StatusIcon />
                <span style={{ fontSize: 13, color: isDark ? '#fff' : 'var(--text-primary)', flex: 1, lineHeight: 1.35 }}>
                    {item.text}
                    {item.povinne && <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>}
                </span>

                {typ === 'checkbox' && (
                    <button type="button" disabled={disabled} onClick={() => onSaveNow(ch.id, item.id, { hodnota_bool: !odp.hodnota_bool })}
                        style={{
                            width: 24, height: 24, borderRadius: 5, flexShrink: 0,
                            background: odp.hodnota_bool ? '#10B981' : 'transparent',
                            border: `2px solid ${odp.hodnota_bool ? '#10B981' : isDark ? 'rgba(255,255,255,0.25)' : 'var(--border-default)'}`,
                            cursor: disabled ? 'default' : 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                        }}>
                        {odp.hodnota_bool && <Check size={14} strokeWidth={3} />}
                    </button>
                )}

                {typ === 'kontrola' && (
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                        {['OK', 'NOK', 'N/A'].map(m => {
                            const sel = odp.hodnota_vyber === m;
                            const col = m === 'OK' ? '#10B981' : m === 'NOK' ? '#EF4444' : '#6B7280';
                            return (
                                <button key={m} type="button" disabled={disabled}
                                    onClick={() => onSaveNow(ch.id, item.id, { hodnota_vyber: m })}
                                    style={{
                                        fontSize: 11, padding: '4px 10px', borderRadius: 5, fontWeight: 600,
                                        background: sel ? `${col}22` : 'transparent',
                                        border: `1px solid ${sel ? col : isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)'}`,
                                        color: sel ? col : mutedColor,
                                        cursor: disabled ? 'default' : 'pointer',
                                    }}>{m}</button>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Parametr (cislo) */}
            {typ === 'cislo' && (() => {
                const instr = item.parametr?.instrukce_mereni || item.instrukce_mereni;
                return (
                    <div style={{ marginLeft: 28, marginTop: 6 }}>
                        {instr && (
                            <p style={{ fontSize: 11, color: isDark ? 'rgba(180,122,234,0.7)' : '#7c3aed', margin: '0 0 6px', fontStyle: 'italic' }}>📋 {instr}</p>
                        )}
                        {/* Měření: okamžitá kontrola při psaní (minule, limit, přírůstek, překlep) */}
                        <MereniPole item={item} odp={odp} checklistId={ch.id} disabled={disabled} isDark={isDark}
                            mutedColor={mutedColor} mimoLimit={mimoLimit}
                            onSave={(data) => onSaveNow(ch.id, item.id, data)} />
                    </div>
                );
            })()}

            {typ === 'vyber' && (
                <div style={{ marginLeft: 28, marginTop: 6, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {moznosti.map(m => {
                        const sel = odp.hodnota_vyber === m.text;
                        const neg = !!m.negativni;
                        const col = neg ? '#EF4444' : '#B47AEA';
                        return (
                            <button key={m.text} type="button" disabled={disabled}
                                onClick={() => onSaveNow(ch.id, item.id, { hodnota_vyber: m.text })}
                                style={{
                                    fontSize: 11, padding: '4px 12px', borderRadius: 5, fontWeight: sel ? 600 : 500,
                                    background: sel ? `${col}22` : 'transparent',
                                    border: `1px solid ${sel ? col : isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)'}`,
                                    color: sel ? col : mutedColor,
                                    cursor: disabled ? 'default' : 'pointer',
                                }}>{m.text}</button>
                        );
                    })}
                </div>
            )}

            {typ === 'text' && (
                <div style={{ marginLeft: 28, marginTop: 6 }}>
                    <textarea defaultValue={odp.hodnota_text || ''} disabled={disabled}
                        onChange={e => onSaveDebounced(ch.id, item.id, { hodnota_text: e.target.value })}
                        placeholder="Zadej text…"
                        style={{
                            width: '100%', padding: '8px 10px', borderRadius: 6, fontSize: 13,
                            background: isDark ? 'rgba(0,0,0,0.3)' : 'var(--surface-base)',
                            color: isDark ? '#fff' : 'var(--text-primary)',
                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
                            resize: 'vertical', minHeight: 48, boxSizing: 'border-box',
                        }} />
                </div>
            )}

            {typ === 'foto' && (
                <div style={{ marginLeft: 28, marginTop: 6 }}>
                    <FotoUpload
                        checklistId={ch.id}
                        polozkaId={item.id}
                        fotoCesta={odp.foto_cesta}
                        disabled={disabled}
                        isDark={isDark}
                        mutedColor={mutedColor}
                        onUploaded={() => onSaveNow(ch.id, item.id, { hodnota_bool: true })}
                    />
                </div>
            )}

            {/* NOK reakce — zobrazuje se VŽDY, když je položka v negativním stavu.
                Obsahuje instrukci (co dělat) a textareu s poznamka_akce (povinný popis). */}
            {jeNok && (() => {
                const instr = typ === 'cislo'
                    ? (item.parametr?.aktualni_limit?.instrukce_min || item.parametr?.aktualni_limit?.instrukce_max || item.instrukce_mereni)
                    : item.instrukce_mereni;
                const title = jeNokKontrola ? 'Kontrola nesplněna'
                    : jeNegativniVyber ? `Negativní odpověď: ${odp.hodnota_vyber}`
                    : 'Hodnota mimo limit';
                return (
                    <div style={{
                        marginLeft: 28, marginTop: 8, padding: '10px 12px', borderRadius: 7,
                        background: isDark ? 'rgba(239,68,68,0.08)' : 'rgba(239,68,68,0.04)',
                        border: '1px solid rgba(239,68,68,0.20)',
                    }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#EF4444', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <AlertTriangle size={13} /> {title}
                        </div>
                        {instr && (
                            <p style={{ fontSize: 11, margin: '0 0 8px', color: isDark ? 'rgba(255,255,255,0.75)' : '#374151', lineHeight: 1.4 }}>
                                {instr}
                            </p>
                        )}
                        <label style={{ fontSize: 10, fontWeight: 600, color: '#EF4444', display: 'block', marginBottom: 4 }}>
                            Co se stalo / provedená akce
                        </label>
                        <textarea
                            defaultValue={odp.poznamka_akce || ''}
                            disabled={disabled}
                            onChange={e => onSaveDebounced(ch.id, item.id, { poznamka_akce: e.target.value })}
                            placeholder="Popiš situaci a jakou akci jsi provedl…"
                            style={{
                                width: '100%', padding: '8px 10px', borderRadius: 6, fontSize: 12,
                                background: isDark ? 'rgba(239,68,68,0.05)' : '#fff',
                                color: isDark ? '#fff' : 'var(--text-primary)',
                                border: '1px solid rgba(239,68,68,0.25)',
                                resize: 'vertical', minHeight: 48, boxSizing: 'border-box',
                            }}
                        />
                    </div>
                );
            })()}

            {/* Obecná poznámka — jen když NENÍ NOK stav (tam je poznámka součástí NOK reakce). */}
            {!jeNok && !isPreview && (
                <PolozkaNote
                    poznamkaAkce={odp.poznamka_akce}
                    disabled={disabled}
                    isDark={isDark}
                    mutedColor={mutedColor}
                    onSaveDebounced={(val) => onSaveDebounced(ch.id, item.id, { poznamka_akce: val })}
                />
            )}
        </div>
    );
}

// Skrytelná textarea pro volitelnou poznámku k položce (mimo NOK stav).
function PolozkaNote({ poznamkaAkce, disabled, isDark, mutedColor, onSaveDebounced }) {
    const hasNote = !!poznamkaAkce;
    const [open, setOpen] = useState(hasNote);

    if (!open) {
        return (
            <button type="button" onClick={() => setOpen(true)}
                style={{
                    marginLeft: 28, marginTop: 6,
                    fontSize: 11, background: 'none', border: 'none',
                    color: isDark ? 'rgba(255,255,255,0.35)' : 'var(--text-muted)',
                    cursor: 'pointer', padding: '2px 0',
                }}>
                + Poznámka
            </button>
        );
    }

    return (
        <div style={{ marginLeft: 28, marginTop: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <label style={{ fontSize: 10, fontWeight: 600, color: mutedColor, textTransform: 'uppercase', letterSpacing: 0.3 }}>Poznámka</label>
                {!hasNote && !disabled && (
                    <button type="button" onClick={() => setOpen(false)}
                        style={{ fontSize: 10, color: mutedColor, background: 'none', border: 'none', cursor: 'pointer' }}>Skrýt</button>
                )}
            </div>
            <textarea
                defaultValue={poznamkaAkce || ''}
                disabled={disabled}
                onChange={e => onSaveDebounced(e.target.value)}
                placeholder="Volitelná poznámka…"
                style={{
                    width: '100%', padding: '6px 10px', borderRadius: 6, fontSize: 12,
                    background: isDark ? 'rgba(0,0,0,0.3)' : 'var(--surface-base)',
                    color: isDark ? '#fff' : 'var(--text-primary)',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
                    resize: 'vertical', minHeight: 36, boxSizing: 'border-box',
                }}
            />
        </div>
    );
}

// Foto upload s camera capture (mobil: kamera, desktop: file picker).
function FotoUpload({ checklistId, polozkaId, fotoCesta, disabled, isDark, mutedColor, onUploaded }) {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [url, setUrl] = useState(fotoCesta ? `/storage/${fotoCesta}` : null);
    const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

    const handleUpload = async (file) => {
        if (!file) return;
        setUploading(true);
        const fd = new FormData();
        fd.append('foto', file);
        fd.append('polozka_id', polozkaId);
        try {
            const res = await fetch(`/api/checklisty/${checklistId}/foto`, {
                method: 'POST',
                headers: {
                    'X-CSRF-TOKEN': csrf(),
                    // Bez Accept: application/json Laravel při validační chybě
                    // vrací HTML (redirect na login) — JSON.parse pak padá.
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: fd,
            });
            const text = await res.text();
            let data = {};
            try { data = text ? JSON.parse(text) : {}; } catch { /* nejspíš HTML — necháme prázdné */ }
            if (res.ok) {
                setUrl(data.foto_url);
                onUploaded?.();
            } else {
                // Laravel validation errors jsou v `data.errors` (objekt: field → [msgs])
                let msg = data.error || data.message;
                if (data.errors && typeof data.errors === 'object') {
                    const flat = Object.values(data.errors).flat().filter(Boolean);
                    if (flat.length) msg = flat.join('\n');
                }
                alert(msg || `Nahrání fotky selhalo (HTTP ${res.status})`);
            }
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    return (
        <div>
            {url ? (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                    <img src={url} alt="Foto"
                        style={{
                            maxWidth: 240, maxHeight: 180, borderRadius: 7, objectFit: 'cover',
                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                        }} />
                    {!disabled && (
                        <button type="button" onClick={() => fileRef.current?.click()}
                            style={{
                                position: 'absolute', bottom: 6, right: 6,
                                fontSize: 10, padding: '4px 10px', borderRadius: 5,
                                background: 'rgba(0,0,0,0.65)', color: '#fff', border: 'none', cursor: 'pointer',
                            }}>
                            Změnit
                        </button>
                    )}
                </div>
            ) : (
                <button type="button" onClick={() => fileRef.current?.click()} disabled={disabled || uploading}
                    style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        width: '100%', padding: '14px', borderRadius: 7,
                        border: `2px dashed ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'}`,
                        background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                        color: mutedColor, fontSize: 12, cursor: (disabled || uploading) ? 'default' : 'pointer',
                    }}>
                    📷 {uploading ? 'Nahrávám…' : 'Vyfotit / nahrát fotku'}
                </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" capture="environment"
                style={{ display: 'none' }}
                onChange={e => handleUpload(e.target.files?.[0])} />
        </div>
    );
}
